import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { TrendingUpIcon, AlertTriangleIcon, UsersIcon, ClipboardCheckIcon, ActivityIcon, BarChart3Icon, MoreVerticalIcon } from 'lucide-react';
export function IPCDashboard() {
  const {
    colors
  } = useTheme();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const stats = [{
    label: 'Overall Compliance',
    value: '87%',
    icon: TrendingUpIcon,
    color: colors.success,
    trend: '+5%'
  }, {
    label: 'Active Alerts',
    value: '12',
    icon: AlertTriangleIcon,
    color: colors.warning,
    trend: '-3'
  }, {
    label: 'Staff Monitored',
    value: '156',
    icon: UsersIcon,
    color: colors.accent,
    trend: '+8'
  }, {
    label: 'Checklists Today',
    value: '89',
    icon: ClipboardCheckIcon,
    color: colors.success,
    trend: '+12'
  }];
  const wardCompliance = [{
    ward: 'Surgical Ward',
    compliance: 92,
    color: colors.success
  }, {
    ward: 'Medical Ward',
    compliance: 85,
    color: colors.accent
  }, {
    ward: 'A&E',
    compliance: 78,
    color: colors.warning
  }, {
    ward: 'TB Ward',
    compliance: 95,
    color: colors.success
  }, {
    ward: 'Isolation Ward',
    compliance: 88,
    color: colors.accent
  }, {
    ward: 'Labour Ward',
    compliance: 81,
    color: colors.warning
  }];
  const recentIncidents = [{
    id: '1',
    type: 'critical',
    title: 'Hand hygiene non-compliance',
    ward: 'A&E',
    time: '15 mins ago',
    staff: 'Dr. James Wilson'
  }, {
    id: '2',
    type: 'warning',
    title: 'PPE protocol deviation',
    ward: 'Surgical Ward',
    time: '1 hour ago',
    staff: 'Nurse Mary Johnson'
  }, {
    id: '3',
    type: 'resolved',
    title: 'Sterilization delay',
    ward: 'Laboratory',
    time: '2 hours ago',
    staff: 'Lab Tech David Lee'
  }];
  const getIncidentColor = (type: string) => {
    switch (type) {
      case 'critical':
        return colors.error;
      case 'warning':
        return colors.warning;
      case 'resolved':
        return colors.success;
      default:
        return colors.textSecondary;
    }
  };
  const handleAction = (action: 'reminder' | 'query', incidentId: string) => {
    setActiveDropdown(null);
    setToastMessage(action === 'reminder' ? 'Reminder sent' : 'Query sent');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };
  return <div className="space-y-6 max-w-6xl mx-auto relative" data-id="element-194">
      {/* Toast Notification */}
      {showToast && <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg animate-slide-in" style={{
      backgroundColor: colors.success,
      color: '#FFFFFF'
    }} data-id="element-195">
          {toastMessage}
        </div>}
      <div data-id="element-196">
        <h1 className="text-2xl font-bold mb-2" style={{
        color: colors.textPrimary
      }} data-id="element-197">
          IPC Officer Dashboard
        </h1>
        <p style={{
        color: colors.textSecondary
      }} data-id="element-198">
          Monitor and manage infection prevention & control across all wards
        </p>
      </div>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-199">
        {stats.map((stat, index) => {
        const Icon = stat.icon;
        return <div key={index} className="p-4 rounded-xl" style={{
          backgroundColor: colors.cardSurface
        }} data-id="element-200">
              <div className="flex items-center justify-between mb-2" data-id="element-201">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
              backgroundColor: stat.color + '20'
            }} data-id="element-202">
                  <Icon size={20} style={{
                color: stat.color
              }} data-id="element-203" />
                </div>
                <span className="text-sm font-medium" style={{
              color: stat.color
            }} data-id="element-204">
                  {stat.trend}
                </span>
              </div>
              <div className="text-2xl font-bold mb-1" style={{
            color: colors.textPrimary
          }} data-id="element-205">
                {stat.value}
              </div>
              <div className="text-sm" style={{
            color: colors.textSecondary
          }} data-id="element-206">
                {stat.label}
              </div>
            </div>;
      })}
      </div>
      {/* Ward Compliance */}
      <div className="p-6 rounded-xl" style={{
      backgroundColor: colors.cardSurface
    }} data-id="element-207">
        <div className="flex items-center justify-between mb-6" data-id="element-208">
          <h2 className="text-xl font-bold" style={{
          color: colors.textPrimary
        }} data-id="element-209">
            Ward Compliance
          </h2>
          <BarChart3Icon size={24} style={{
          color: colors.accent
        }} data-id="element-210" />
        </div>
        <div className="space-y-4" data-id="element-211">
          {wardCompliance.map((ward, index) => <div key={index} data-id="element-212">
              <div className="flex items-center justify-between mb-2" data-id="element-213">
                <span style={{
              color: colors.textPrimary
            }} data-id="element-214">
                  {ward.ward}
                </span>
                <span className="font-bold" style={{
              color: ward.color
            }} data-id="element-215">
                  {ward.compliance}%
                </span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{
            backgroundColor: colors.divider
          }} data-id="element-216">
                <div className="h-full rounded-full transition-all" style={{
              width: `${ward.compliance}%`,
              backgroundColor: ward.color
            }} data-id="element-217" />
              </div>
            </div>)}
        </div>
      </div>
      {/* Recent Incidents */}
      <div className="p-6 rounded-xl" style={{
      backgroundColor: colors.cardSurface
    }} data-id="element-218">
        <div className="flex items-center justify-between mb-6" data-id="element-219">
          <h2 className="text-xl font-bold" style={{
          color: colors.textPrimary
        }} data-id="element-220">
            Recent Incidents
          </h2>
          <ActivityIcon size={24} style={{
          color: colors.accent
        }} data-id="element-221" />
        </div>
        <div className="space-y-3" data-id="element-222">
          {recentIncidents.map(incident => <div key={incident.id} className="p-4 rounded-lg border-l-4 relative" style={{
          backgroundColor: colors.background,
          borderLeftColor: getIncidentColor(incident.type)
        }} data-id="element-223">
              <div className="flex items-start justify-between mb-2" data-id="element-224">
                <div className="flex-1" data-id="element-225">
                  <div className="font-semibold mb-1" style={{
                color: getIncidentColor(incident.type)
              }} data-id="element-226">
                    {incident.title}
                  </div>
                  <span className="text-xs px-2 py-1 rounded inline-block" style={{
                backgroundColor: getIncidentColor(incident.type) + '20',
                color: getIncidentColor(incident.type)
              }} data-id="element-227">
                    {incident.type}
                  </span>
                </div>
                {(incident.type === 'critical' || incident.type === 'warning') && <div className="relative" data-id="element-228">
                    <button onClick={() => setActiveDropdown(activeDropdown === incident.id ? null : incident.id)} className="p-2 rounded-lg hover:bg-opacity-10 transition-colors" style={{
                backgroundColor: colors.divider,
                color: colors.textPrimary
              }} data-id="element-229">
                      <MoreVerticalIcon size={18} data-id="element-230" />
                    </button>
                    {activeDropdown === incident.id && <>
                        <div className="fixed inset-0 z-10" onClick={() => setActiveDropdown(null)} data-id="element-231" />
                        <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg z-20 overflow-hidden" style={{
                  backgroundColor: colors.cardSurface,
                  border: `1px solid ${colors.divider}`
                }} data-id="element-232">
                          <button onClick={() => handleAction('reminder', incident.id)} className="w-full px-4 py-3 text-left hover:bg-opacity-5 transition-colors" style={{
                    color: colors.textPrimary
                  }} data-id="element-233">
                            Send Reminder
                          </button>
                          <div className="h-px" style={{
                    backgroundColor: colors.divider
                  }} data-id="element-234" />
                          <button onClick={() => handleAction('query', incident.id)} className="w-full px-4 py-3 text-left hover:bg-opacity-5 transition-colors" style={{
                    color: colors.textPrimary
                  }} data-id="element-235">
                            Send Query
                          </button>
                        </div>
                      </>}
                  </div>}
              </div>
              <div className="text-sm space-y-1" style={{
            color: colors.textSecondary
          }} data-id="element-236">
                <div data-id="element-237">
                  {incident.ward} • {incident.time}
                </div>
                <div data-id="element-238">Staff: {incident.staff}</div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}